/**
 * Un champ et son formulaire — étiquette, marquage, aide et erreur, tous reliés.
 *
 * Le contrôle reste choisi par l'appelant (`Input`, `Textarea`, `SelectNatif`) : cette
 * enveloppe ne fournit que ce qui manquait — l'identifiant partagé entre l'étiquette,
 * l'aide et le message d'erreur, sans lequel `aria-describedby` n'a rien à désigner.
 */
import type { JSX, ReactNode } from 'react'

import { Label } from '@/components/ui/label'
import { useT } from '@/i18n/language'
import { cn } from '@/lib/utils'
import type { ControleChamp, EtatChamp, Formulaire, Schema } from '@/hooks/useFormulaire'

export function Champ({ champ, children, className, labelCache }: {
  champ: EtatChamp
  children: (controle: ControleChamp) => ReactNode
  className?: string
  /**
   * Étiquette rendue en `sr-only` : pour les formulaires en ligne où la place manque.
   * L'étiquette EXISTE quand même — un `aria-label` répété sur quarante cartes rend la
   * navigation par étiquette inutilisable, faute de pouvoir distinguer deux champs.
   */
  labelCache?: boolean
}): JSX.Element {
  const { t } = useT()
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={champ.id} className={labelCache ? 'sr-only' : undefined}>
        {champ.label}
        {champ.obligatoire ? (
          <>
            {/* L'astérisque est décoratif : sa signification passe par `aria-required` sur
                le contrôle, et par ce texte pour qui lit l'étiquette à voix haute. */}
            <span aria-hidden="true" className="text-destructive"> *</span>
            <span className="sr-only"> {t('champ.obligatoireSr', '(obligatoire)')}</span>
          </>
        ) : null}
      </Label>

      {children(champ.controle)}

      {champ.aide ? (
        <p id={`${champ.id}-aide`} className="text-xs text-muted-foreground">{champ.aide}</p>
      ) : null}

      {/* Pas de `role="alert"` ici : N champs invalides produiraient N annonces simultanées,
          illisibles. C'est le récapitulatif qui alerte, et le focus déplacé sur le premier
          champ fautif fait lire SON message via `aria-describedby`. */}
      {champ.erreur ? (
        <p id={`${champ.id}-err`} data-testid={`err-${champ.nom}`}
           className="text-xs font-medium text-destructive">{champ.erreur}</p>
      ) : null}
    </div>
  )
}

/**
 * Le `<form>` : légende d'obligation, soumission validée, récapitulatif d'erreurs.
 *
 * Générique sur le schéma pour que `onValide` reçoive les valeurs RÉELLEMENT typées du
 * formulaire — un `never` obligerait chaque appelant à recaster, et le premier oubli de
 * champ passerait alors inaperçu.
 */
export function FormulaireChamps<S extends Schema>({ f, onValide, children, className }: {
  f: Formulaire<S>
  onValide: (valeurs: { [K in keyof S]: string }) => void
  children: ReactNode
  className?: string
}): JSX.Element {
  const { t } = useT()
  return (
    // `noValidate` : l'infobulle native du navigateur n'est ni traduite ni stylée, et elle
    // court-circuiterait nos messages. L'ARIA et les phrases ci-dessous font foi.
    <form noValidate className={cn('space-y-4', className)} onSubmit={f.soumettre(onValide)}>
      <p className="text-xs text-muted-foreground">
        {f.tousObligatoires
          ? t('form.tousObligatoires', 'Tous les champs sont obligatoires.')
          : <>
              <span aria-hidden="true" className="text-destructive">*</span>{' '}
              {t('form.legende', 'champ obligatoire')}
            </>}
      </p>

      {children}

      {f.soumis && f.resume.length > 0 ? (
        <div role="alert" data-testid="form-errors"
             className="rounded-md border border-destructive/50 bg-destructive/5 p-2 text-sm text-destructive">
          {t('form.resume', '{{n}} champ à corriger : {{liste}}',
             { n: f.resume.length, liste: f.resume.join(', ') })}
        </div>
      ) : null}
    </form>
  )
}
